import axios from 'axios';
import { getOrganizationId } from '../organization';
import { ensureAiCreditsForTrigger } from '../aiCreditsGuard';
import { getAiAnalyticsBaseUrl } from '../aiAnalyticsUrl';

export interface AnalysisItem {
    phone_number: string;
    conversation_id: string;
    emotion: string;
    emoji: string;
    confidence: number;
    summary: string;
    score: number;
    conversation_stage: string;
}

export interface BehaviorAnalysisResponse {
    business_account_id: string;
    analyses: AnalysisItem[];
    tags: any[];
    metadata: {
        total_messages: number;
        model: string;
        generation_time_seconds: number;
        input_tokens: number;
        output_tokens: number;
        total_tokens: number;
        total_cost_usd: number;
    };
    total_contacts: number;
    current_page: number;
    page_size: number;
    total_pages: number;
}

// Create a dedicated client for the AI suggestions API
const suggestionsClient = axios.create({
    baseURL: getAiAnalyticsBaseUrl('api'),
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add request interceptor to include auth token if available (assuming it shares auth)
// If the user's curl didn't have it, it might not be needed, but safe to check.
// For now, I will NOT add auth if the curl didn't have it, but usually it's needed.
// actually the user said "ai has different api endpoint", I will stick to what the curl shows.
// The curl didn't show auth. But I'll assume it might be needed or maybe not.
// Let's stick to the curl exactly for now.
// Actually, if I look at the previous logs, standard requests usually have Bearer tokens.
// If I use the standard `apiClient` structure but just change the URL, it might be better if I CAN sharing the token.
// PROPOSAL: Use `axios` directly for this separate endpoint.

// Define filters interface based on what's used in Inbox
export interface BehaviorAnalysisFilters {
    search?: string;
    status?: string;
    priority?: 'high' | 'medium' | 'low';
    isRequested?: boolean;
    isSteppedIn?: boolean;
}

export interface SuggestionItem {
    reply: string;
    tone: string;
    rationale: string;
}

export interface SuggestedResponsesResponse {
    business_account_id: string;
    phone_number: string;
    suggestions: SuggestionItem[];
    metadata: {
        total_messages: number;
        model: string;
        generation_time_seconds: number;
        input_tokens: number;
        output_tokens: number;
        total_tokens: number;
        total_cost_usd: number;
    };
}

export const suggestionsApi = {
    getBehaviorAnalysis: async (businessAccountId: string, page = 1, limit = 100, filters: BehaviorAnalysisFilters = {}) => {
        await ensureAiCreditsForTrigger({ businessAccountId });
        const organizationId = getOrganizationId();
        return suggestionsClient.post<BehaviorAnalysisResponse>('/suggestion/behavior', {
            business_account_id: businessAccountId,
            ...(organizationId ? { organization_id: organizationId } : {}),
            limit: limit,
            page: page,
            page_size: limit, // mapping limit to page_size as well to be safe or just standard
            // Spread filters into the payload
            // Mapping frontend filter keys to likely API keys (snake_case usually)
            search: filters.search,
            status: filters.status,
            priority: filters.priority,
            isRequested: filters.isRequested, // Changed from is_requested to match curl
            is_stepped_in: filters.isSteppedIn
        });
    },

    getSuggestedResponses: async (businessAccountId: string, phoneNumber: string) => {
        await ensureAiCreditsForTrigger({ businessAccountId });
        const organizationId = getOrganizationId();
        return suggestionsClient.post<SuggestedResponsesResponse>('/suggestion/responses', {
            business_account_id: businessAccountId,
            ...(organizationId ? { organization_id: organizationId } : {}),
            phone_number: phoneNumber,
            limit: 30 // Hardcoded as per curl example, or could be passed
        });
    },

    getInstagramSuggestions: async (businessAccountId: string, threadId: string) => {
        return suggestionsClient.post<{ suggestions: SuggestionItem[]; metadata: Record<string, unknown> }>(
            '/suggestion/instagram/responses',
            {
                business_account_id: businessAccountId,
                thread_id: threadId,
                limit: 30
            }
        );
    },

    getFacebookSuggestions: async (businessAccountId: string, threadId: string) => {
        return suggestionsClient.post<{ suggestions: SuggestionItem[]; metadata: Record<string, unknown> }>(
            '/suggestion/facebook/responses',
            {
                business_account_id: businessAccountId,
                thread_id: threadId,
                limit: 30
            }
        );
    }
};
